import { formatMpxc } from '@pxc/shared';

/**
 * Display formatting. Money always comes pre-formatted from the server; these
 * helpers cover time, dates and the few places the client re-formats a live
 * value (an accruing balance, a running countdown).
 */

export { formatMpxc };

/** "3h 24m", "24m 10s", "48s" — compact and never longer than two units. */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;

  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
}

/** "04:12:33" — the fixed-width form used inside the mine. */
export function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(rest)}` : `${pad(minutes)}:${pad(rest)}`;
}

/**
 * "just now", "12m ago", "3h ago", "Aug 24".
 *
 * `translate` is the reader's translator. It is optional because this module
 * has no React around it and is called from places that have no provider —
 * without one the English forms are used, which is the same fallback the
 * catalogue itself has.
 */
export function formatRelativeTime(
  timestamp: number,
  now: number = Date.now(),
  translate?: (key: string, values?: Record<string, string | number>) => string,
): string {
  const deltaSeconds = Math.floor((now - timestamp) / 1000);
  const say = (key: string, count?: number, fallback?: string): string =>
    translate ? translate(key, count === undefined ? undefined : { count }) : (fallback ?? key);

  if (deltaSeconds < 45) return say('time.justNow', undefined, 'just now');
  if (deltaSeconds < 3600) {
    const count = Math.floor(deltaSeconds / 60);
    return say('time.minutesAgo', count, `${count}m ago`);
  }
  if (deltaSeconds < 86_400) {
    const count = Math.floor(deltaSeconds / 3600);
    return say('time.hoursAgo', count, `${count}h ago`);
  }
  if (deltaSeconds < 7 * 86_400) {
    const count = Math.floor(deltaSeconds / 86_400);
    return say('time.daysAgo', count, `${count}d ago`);
  }
  return formatDate(timestamp);
}

export function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatFullDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "4h", "2m", "45s" — the compact form used where width is tight. */
export function formatShiftShort(seconds: number): string {
  if (seconds >= 3600) {
    const hours = seconds / 3600;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
  }
  if (seconds >= 60) return `${Math.round(seconds / 60)}m`;
  return `${seconds}s`;
}

/**
 * "4h shift", "2m shift" — used where there is room for the noun, in the
 * reader's language when given their translator (as formatRelativeTime).
 */
export function formatShiftLength(
  seconds: number,
  translate?: (key: string, values?: Record<string, string | number>) => string,
): string {
  const length = formatShiftShort(seconds);
  return translate ? translate('time.shiftLength', { length }) : `${length} shift`;
}

/** "12h 40m mined" from a lifetime seconds counter. */
export function formatMiningTime(seconds: number): string {
  if (seconds <= 0) return '0h';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'PX';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '')[0] ?? ''}${(parts[1] ?? '')[0] ?? ''}`.toUpperCase();
}
